import { SessionModel } from '@/models/auth/session';
import { MarkerDataModel } from '@/models/marker/marker';
import { MarkerListModel } from '@/models/marker/marker-list';
import { RouteLessonsModel, RouteMilestoneModel, SuggestionsModel } from '@/models/marker/reroute';
import { LearnerPreference } from '@/models/proficiency/learner-preference';
import { learnerAPI } from '@/providers/apis/learner/learner';
import { learningRegistryAPI } from '@/providers/apis/learning-registry/learning-registry';
import { markersAPI } from '@/providers/apis/markers/markers';
import { portfolioAPI } from '@/providers/apis/portfolio/portfolio';
import { profileAPI } from '@/providers/apis/profile/profile';
import { searchAPI } from '@/providers/apis/search/search';
import { struggleAPI } from '@/providers/apis/struggle/struggle';
import { sessionService } from '@/providers/services/auth/session';
import { LEARNER_PREFERENCE_META } from '@/utils/constants';
import moment from 'moment';
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import CatalogPullOut from '../catalog-pull-out/catalog-pull-out';
import ContentInfoComponent from '../content-info/content-info';
import ECPAInfoComponent from '../ecpa-info/ecpa-info';
import LearnerIdentityComponent from '../learner-identity/learner-identity';
import MilestoneViewComponent from '../milestone-view/milestone-view';
import PortfolioInfoComponent from '../portfolio-info/portfolio-info';
import ShowcaseForm from './showcase-form';
import axios from 'axios';

@Component({
    name: 'showcase-edit-layout',
    components: {
        'milestone-view': MilestoneViewComponent,
        'learner-identity': LearnerIdentityComponent,
        'publisher-content': ContentInfoComponent,
        'ecpa-content': ECPAInfoComponent,
        'portfolio-content': PortfolioInfoComponent,
        'catalog-pull-out': CatalogPullOut,
    },
})

export default class ShowcaseEditLayoutComponent extends Vue {

    // -----------------------------------------------------------------------
    // Properties

    private showcaseCompetency: any = null;

    private primaryCompetency: any = null;

    @Prop({default: []})
    private generateRouteList!: RouteMilestoneModel[];

    @Prop()
    private activeMarker!: MarkerListModel;

    @Prop()
    private chartData!: any;

    @Prop()
    private userId: any;

    private selectedCollection: any = null;

    private productData: any = null;

    @Prop()
    private activeSubject: any;

    @Prop()
    private isJourneyLoaded!: boolean;

    private get session() {
        return sessionService.getSession();
    }

    private contentTabs: any = [
        {
            name: 'Learner Identity',
            componentName: 'learner-identity',
            isActive: true,
        },
        {
            name: 'Content',
            componentName: 'publisher-content',
        },
        {
            name: 'ECPA',
            componentName: 'ecpa-content',
        },
        {
            name: 'Portfolio',
            componentName: 'portfolio-content',
        },
    ];

    private activeTab: any = this.contentTabs[1];

    private dependentCompetencies: any = [];

    private isLoading: boolean = false;

    private learnerPreferences: LearnerPreference[] = [];

    private dokContent: any = [];

    private collectionData: any = null;

    private showcaseData: {[key: string]: MarkerDataModel} = {} as {[key: string]: MarkerDataModel};

    private editContent: MarkerDataModel = {} as MarkerDataModel;

    private contentList: any = [];

    private portfolioContent: any = [];

    private struggleList: any = [];

    private activeLesson: any = null;

    private suggestedContent: any = {};

    private storedContent: any = {};

    private markerData: any = {};

    private activeMilestone: any = 0;

    public loadPreferenceData() {
        const component = this;
        const requestParams = component.getRequestParams();
        const learnerPreferences: LearnerPreference[] = [];
        const {learner} = this.editContent;
        const resourcePref = learner.resourcePref || {};
        axios.all([
            learnerAPI.fetchLearnerPreferences(requestParams),
            this.fetchUserPreference(),
        ]).then(axios.spread((preferences: LearnerPreference[], learnerPrefs: any) => {
            const resourceTypePrefs = learnerPrefs ? learnerPrefs.resourceTypePrefs : null;
            preferences.map( (preference: LearnerPreference) => {
                const defaultPrefs = resourceTypePrefs && resourceTypePrefs[`${preference.label}_resource`] ?
                    resourceTypePrefs[`${preference.label}_resource`] : preference.value;
                learnerPreferences.push({
                  label: LEARNER_PREFERENCE_META[preference.label].labelKey,
                  icon: LEARNER_PREFERENCE_META[preference.label].icon,
                  keyName: preference.label,
                  value: (defaultPrefs >= 0 ? defaultPrefs : resourcePref[preference.label]) as any,
                });
              });
            component.learnerPreferences = learnerPreferences;
        }));
    }

    // -----------------------------------------------------------------------
    // Hooks

    private created() {
        this.isLoading = true;
        this.editContent = new ShowcaseForm(this.userId);
        this.fetchSubscribedProduct();
        if (this.showcaseCompetency) {
            this.loadData();
        }
    }

    // -----------------------------------------------------------------------
    // Actions

    private onSelectTab(tab: any) {
        this.activeTab = tab;
    }

    private onClosePullup() {
        this.$emit('onClose');
    }

    private onShowMetadata(assessmentData: any) {
        this.collectionData = assessmentData;
        (this.$el.querySelector('.catalog-pull-out-container') as HTMLElement).style.left = 'calc(100vw - 450px)';
    }

    private onSelectCompetency(collection: any, lesson = null, milestoneIndex = null) {
        const competency = collection.gutCodes;
        this.selectedCollection = collection;
        this.activeLesson = lesson;
        this.activeMilestone = milestoneIndex;
        const chartData = this.chartData;
        for (const domain of chartData) {
            for (const topic of domain.topics) {
                const content = competency ? topic.competencies.find(
                    (compData: any) => compData.competencyCode === competency[0]) : null;
                if (content) {
                    this.onSelectTab(this.contentTabs[0]);
                    this.showcaseCompetency = content;
                    this.primaryCompetency = content;
                    if (!this.showcaseData[content.competencyCode]) {
                        this.showcaseData[content.competencyCode] = new ShowcaseForm(this.userId);
                        this.showcaseData[content.competencyCode].competency.masteryStatus = content.competencyStatus;
                    }
                    this.editContent = this.showcaseData[content.competencyCode];
                    this.loadData();
                    break;
                }
            }
        }
    }

    private onApply() {
        if (!this.primaryCompetency) {
            this.markerData[this.activeMarker.id] = this.productData;
            this.$emit('onChangeContent', this.productData);
        } else {
            this.updateRerouteMetadata();
        }
    }

    private onEditDependent(competency: any) {
        this.showcaseCompetency = competency;
        if (!this.showcaseData[competency.competencyCode]) {
            this.showcaseData[competency.competencyCode] = new ShowcaseForm(this.userId);
            this.showcaseData[competency.competencyCode].competency.masteryStatus = competency.competencyStatus;
        }
        this.editContent = this.showcaseData[competency.competencyCode];
        this.fetchStrugglesList();
        this.loadPreferenceData();
        this.fetchDokContent();
        this.fetchPortfolioContent();
    }

    private onChangeContent(productData: any) {
       this.productData = productData;
    }

    private resetData() {
        this.productData = this.markerData[this.activeMarker.id];
        const storageData = this.showcaseCompetency ? this.storedContent[this.showcaseCompetency.competencyCode] : null;
        this.editContent = storageData ? storageData : new ShowcaseForm(this.userId);
        if (this.selectedCollection && !storageData) {
            this.editContent.competency.masteryStatus = this.showcaseCompetency.competencyStatus;
        }
        this.loadData();
    }

    // -----------------------------------------------------------------------
    // Methods

    private onLoadLearningContent() {
        const chartData = this.chartData;
        this.dependentCompetencies = [];
        const competencyCode = this.showcaseCompetency.competencyCode;
        searchAPI.fetchLearningMapContents(competencyCode).then((contents) => {
            const dependentCompetency = contents.prerequisites.reverse();
            for (const domain of chartData) {
                for (const topic of domain.topics) {
                    for (const competency of topic.competencies) {
                       let dependentComp: any = dependentCompetency.find(
                           (item) => item.id === competency.competencyCode);
                       if (dependentComp) {
                           dependentComp.score = 0.8;
                           dependentComp = Object.assign({}, dependentComp, competency);
                           this.dependentCompetencies.push(dependentComp);
                       }
                    }
                }
            }
        });
    }

    private fetchStrugglesList() {
        const params = {
            compCodes: [this.showcaseCompetency.competencyCode],
        };
        struggleAPI.fetchStruggleList(this.activeSubject.code, params)
        .then((struggleContent) => {
            this.struggleList = struggleContent.struggles;
        });
    }

    private getRequestParams() {
        return {
            user: this.userId,
            month: moment().format('MM'),
            year: moment().format('YYYY'),
        };
    }

    private fetchDokContent() {
        profileAPI.fetchDok().then((dok) => {
            this.dokContent = dok.depth_of_knowledge;
        });
    }

    private loadData() {
        this.loadPreferenceData();
        this.fetchDokContent();
        this.fetchUserPreference();
        if (this.showcaseCompetency) {
            this.onLoadLearningContent();
            this.fetchStrugglesList();
            this.fetchPortfolioContent();
        }
    }

    private fetchSubscribedProduct() {
        const params = {
            offset: 0,
            limit: 20,
            registry_type: 'content_registry',
            search_terms: '',
        };
        learningRegistryAPI.fetchSubscribedToolRegistryList(params).then((registryList: any) => {
            this.contentList = registryList;
        });
    }

    private fetchPortfolioContent() {
        const competency = this.showcaseCompetency;
        const requestParams = {
            gutCode: competency.competencyCode,
            activityType: 'assessment',
            userId: this.userId,
            offset: 0,
            limit: 10,
          };
        portfolioAPI.fetchPortfolioContents(requestParams, 'competency').then((content) => {
            this.portfolioContent = content;
            this.portfolioContent.push({...this.selectedCollection});
        });
    }

    private buildRequestPayload(collection: any) {
       const primaryCompetency = this.showcaseCompetency;
       const dependentComp = this.dependentCompetencies;
       const { learner, context, content, activity, competency} = this.editContent;
       learner.confidence = parseFloat(learner.confidence);
       content.attempt = parseInt(content.attempt, 0);
       context.markerId = this.activeMarker.id;
       context.contextCollectionId = collection.id;
       context.contextCollectionType = collection.format;
       const format = collection.format || collection.type;
       activity.type = ['assessment-external', 'assessment'].includes(format) ? 'assessment.complete' : 'collection.complete';
       activity.activityTime = moment().valueOf();
       content.attemptId = collection.attemptId || '';
       content.contentId = collection.id;
       content.questionCount = collection.questionCount;
       content.contentType = collection.format;
       context.tenantId = this.session!.tenant!.tenant_id;
       competency.competencyCode = primaryCompetency.competencyCode;
       competency.competencySeq = primaryCompetency.competencySeq;
       competency.domainCode = primaryCompetency.domainCode;
       competency.subjectCode = this.activeSubject.code;
       competency.topicCode = primaryCompetency.topicCode;
       competency.topicSeq = primaryCompetency.topicSeq;
       dependentComp.forEach((item: any) => {
           competency.dependency[item.competencyCode] = item.competencyStatus;
       });
    }

    private updateRerouteMetadata() {
        this.updateUserPreference();
        this.buildRequestPayload(this.selectedCollection);
        markersAPI.updateRouteData(this.editContent).then((suggestionContent) => {
            if (this.activeLesson) {
                this.suggestedContent[this.activeLesson.lessonId] = suggestionContent;
            }
            const activeContent = this.portfolioContent.find((item: any) => item.id === this.selectedCollection.id);
            if (activeContent) {
                Object.assign(this.selectedCollection, activeContent);
            }
            this.storedContent[this.showcaseCompetency.competencyCode] = {...this.editContent};
            this.$emit('onChangeContent', this.productData);
        });
    }

    private fetchUserPreference() {
        return profileAPI.fetchUserPreference(this.userId);
    }

    private updateUserPreference() {
        const { learner } = this.editContent;
        const resourcePref = learner.resourcePref;
        const requestBody: any = {};
        Object.keys(resourcePref).forEach((pref) => {
            requestBody[`${pref}_resource`] = resourcePref[pref];
        });
        const params = {
            preference_settings: {
                resource_type_preference: requestBody,
            },
            user_id: this.userId,
        };
        profileAPI.updateUserPreference(params);
    }

}
